export type VoiceStats = {
  codec: string;
  packetSize: number;
  seq: number;
  ts: number;
  pps: number;
  lost: number;
  reordered: number;
  jitterMs: number;
  queueMs: number;
  outRate: number;
  resampleIn: number;
  resampleOut: number;
  underrun: number;
  overrun: number;
  mode: "worklet" | "script" | "native" | "none";
  muted: boolean;
  ws: string;
  captureMode: "worklet" | "script" | "native" | "none";
  captureRate: number;
  captureBlock: number;
  captureSampleRate: number;
  captureChannelCount: number;
  captureEncoding: string;
  pcmFrameSize: number;
  g711Codec: string;
  g711FrameBytes: number;
  pcmMin: number;
  pcmMax: number;
  pcmRMS: number;
  clippingCount: number;
  decodedPcmRms: number;
  audioTrackBufferSize: number;
  playbackQueueSize: number;
  droppedFrames: number;
  malformedFrames: number;
  invalidPayloadCount: number;
  jt1078PayloadLength: number;
  e2eEstMs: number;
  listenAgeMs: number;
  enqueueAt: number;
  playAt: number;
  pttGestureAt: number;
  pttWsOpenMs: number;
  pttMicMs: number;
  pttFirstPcmMs: number;
  pttFirstEncodeMs: number;
  pttFirstSendMs: number;
  pttTalkingMs: number;
  pttPackets: number;
  pttDropped: number;
  pttIntervalMs: number;
  callId: string;
  serverReceiveMs: number;
  listenPacketReceiveMs: number;
  staleFrames: number;
  captureAt: number;
  sendAt: number;
  receiveAt: number;
};

const empty = (): VoiceStats => ({
  codec: "—",
  packetSize: 0,
  seq: -1,
  ts: 0,
  pps: 0,
  lost: 0,
  reordered: 0,
  jitterMs: 0,
  queueMs: 0,
  outRate: 0,
  resampleIn: 0,
  resampleOut: 0,
  underrun: 0,
  overrun: 0,
  mode: "none",
  muted: true,
  ws: "idle",
  captureMode: "none",
  captureRate: 0,
  captureBlock: 0,
  captureSampleRate: 0,
  captureChannelCount: 0,
  captureEncoding: "",
  pcmFrameSize: 0,
  g711Codec: "—",
  g711FrameBytes: 0,
  pcmMin: 0,
  pcmMax: 0,
  pcmRMS: 0,
  clippingCount: 0,
  decodedPcmRms: 0,
  audioTrackBufferSize: 0,
  playbackQueueSize: 0,
  droppedFrames: 0,
  malformedFrames: 0,
  invalidPayloadCount: 0,
  jt1078PayloadLength: 0,
  e2eEstMs: 0,
  listenAgeMs: 0,
  enqueueAt: 0,
  playAt: 0,
  pttGestureAt: 0,
  pttWsOpenMs: 0,
  pttMicMs: 0,
  pttFirstPcmMs: 0,
  pttFirstEncodeMs: 0,
  pttFirstSendMs: 0,
  pttTalkingMs: 0,
  pttPackets: 0,
  pttDropped: 0,
  pttIntervalMs: 0,
  callId: "",
  serverReceiveMs: 0,
  listenPacketReceiveMs: 0,
  staleFrames: 0,
  captureAt: 0,
  sendAt: 0,
  receiveAt: 0,
});

let latest: VoiceStats = empty();

export function audioDebugEnabled(): boolean {
  try {
    if (typeof window === "undefined") return false;
    if (new URLSearchParams(window.location.search).get("audioDebug") === "1") return true;
    if (window.localStorage.getItem("dashcamAudioDebug") === "1") return true;
  } catch {
    /* ignore */
  }
  return Boolean(import.meta.env.DEV);
}

export function publishVoiceStats(partial: Partial<VoiceStats>): VoiceStats {
  latest = { ...latest, ...partial };
  try {
    (window as unknown as { __dashcamAudio?: VoiceStats }).__dashcamAudio = latest;
  } catch {
    /* ignore */
  }
  return latest;
}

export function voiceStats(): VoiceStats {
  return latest;
}

export function formatVoiceStats(s: VoiceStats): string {
  const ptt =
    s.pttFirstSendMs > 0
      ? `ptt send=${s.pttFirstSendMs.toFixed(0)}ms mic=${s.pttMicMs.toFixed(0)} pcm=${s.pttFirstPcmMs.toFixed(0)} enc=${s.pttFirstEncodeMs.toFixed(0)} talking=${s.pttTalkingMs.toFixed(0)}`
      : s.pttGestureAt
        ? "ptt waiting first send"
        : "ptt idle";
  return [
    `${s.codec.toUpperCase()} ${s.packetSize}B seq=${s.seq} pps=${s.pps}`,
    `lost=${s.lost} reorder=${s.reordered} jitter=${s.jitterMs.toFixed(0)}ms q=${s.queueMs.toFixed(0)}ms`,
    `e2e~${s.e2eEstMs.toFixed(0)}ms age=${s.listenAgeMs.toFixed(0)}ms`,
    `${s.mode} ${s.outRate}Hz mute=${s.muted ? 1 : 0} ws=${s.ws}`,
    `under=${s.underrun} over=${s.overrun} rs=${s.resampleIn}→${s.resampleOut}`,
    `cap ${s.captureMode} ${s.captureSampleRate || s.captureRate}Hz ch=${s.captureChannelCount || 1} ${s.captureEncoding || "pcm16"}`,
    `g711 ${s.g711Codec} ${s.g711FrameBytes}B pcm=${s.pcmMin}..${s.pcmMax} rms=${s.pcmRMS} clip=${s.clippingCount}`,
    `play q=${s.playbackQueueSize} drop=${s.droppedFrames} stale=${s.staleFrames} bad=${s.malformedFrames} inv=${s.invalidPayloadCount} decRms=${s.decodedPcmRms}`,
    `ts cap=${s.captureAt || "—"} send=${s.sendAt || "—"} recv=${s.receiveAt || "—"} play=${s.playAt ? s.playAt.toFixed(0) : "—"}`,
    s.callId ? `call=${s.callId.slice(0, 8)}` : "",
    ptt,
    `ptt pps~${s.pttIntervalMs > 0 ? (1000 / s.pttIntervalMs).toFixed(0) : "—"} drop=${s.pttDropped} n=${s.pttPackets}`,
  ].filter(Boolean).join("\n");
}
